import { createAsyncThunk } from '@reduxjs/toolkit'
import type { AxiosError } from 'axios'
import { getAccessToken } from '@privy-io/react-auth'

import { ValidationErrors } from 'store/types'

import { getAxiosInstance } from 'services/api'

import { NewsItem, Request, User } from 'types/app'

import {
  SignInParams,
  SignUpParams,
  ActivationParams,
  UpdateParams,
} from './types'

const api = getAxiosInstance()

export const signInUser = createAsyncThunk<
  User,  // ← Removed { token: string }, Privy manages tokens
  SignInParams,
  { rejectValue: ValidationErrors }
>(
  'auth/signin',
  async ({ id, email, isNewUser, accountAddress }, { rejectWithValue }) => {
    try {
      // The api instance will automatically add the Privy token via interceptor
      const { data } = await api.post('/auth/v2', {
        id,
        email,
        isNewUser,
        accountAddress,
      })
      if (data.success) {
        // Don't return token - Privy manages authentication tokens
        return data.user
      }
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

// export const signInUser = createAsyncThunk<
//   User & { token: string },
//   SignInParams,
//   { rejectValue: ValidationErrors }
// >('auth/signin', async ({ email, password }, { rejectWithValue }) => {
//   try {
//     const { data } = await api.post('/login', {
//       email,
//       password,
//     })
//     return { ...data.user, token: data.token }
//   } catch (err) {
//     const error = err as AxiosError<ValidationErrors>

//     if (!error.response) throw err

//     return rejectWithValue(error.response.data)
//   }
// })

export const signUpUser = createAsyncThunk<
  User,
  SignUpParams,
  { rejectValue: ValidationErrors }
>(
  'auth/signup',
  async (
    { avatar, firstname, lastname, username, email, password },
    { rejectWithValue },
  ) => {
    try {
      const params = new FormData()
      params.append('avatar', avatar)
      params.append('firstname', firstname)
      params.append('lastname', lastname)
      params.append('username', username)
      params.append('email', email)
      params.append('password', password)

      const response = await api.post('/register', params, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      })
      return response.data.user
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

// DEPRECATED: Old activation system - not used with Privy
export const activateUser = createAsyncThunk<
  User,
  ActivationParams,
  { rejectValue: ValidationErrors }
>('auth/activation', async ({ token }, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/activation', {
      token,
    })
    return data.user
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

// export const getUserInfo = async (username: string) => {
//   try {
//     const { data: response } = await api.get(`/user/${username}`)

//     return response.data as {
//       user: User
//       news: NewsItem[]
//       userFollowingGames: any
//       teams: any
//     }
//   } catch (err) {
//     console.log('ERR - ', err)
//   }
// }

export const getUserInfo = createAsyncThunk<
  {
    user: User
    news: NewsItem[]
    userFollowingGames: any
    teams: any
  },
  string,
  { rejectValue: ValidationErrors }
>('auth/getUserInfo', async (username, { rejectWithValue }) => {
  try {
    const response = await api.get(`/user/${username}`)
    return response.data.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const updateUser = createAsyncThunk<
  User & { message: string },  // Removed token - Privy manages tokens
  UpdateParams,
  { rejectValue: ValidationErrors }
>(
  'auth/update',
  async (
    { avatar, cover_photo, firstname, lastname, username, biography },
    { rejectWithValue },
  ) => {
    try {
      const params = new FormData()
      if (avatar) params.append('avatar', avatar)
      if (cover_photo) params.append('cover_photo', cover_photo)
      params.append('firstname', firstname || '')
      params.append('lastname', lastname || '')
      params.append('username', username)
      // Always send biography, even if it's an empty string
      params.append('biography', biography || '')

      const response = await api.put('/me/update', params, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      })
      // Backend returns: { success, user, message } - no token needed
      return { ...response.data.user, message: response.data.message }
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

export const requestToBecomeOrganizer = createAsyncThunk<
  User,  // Removed token - Privy manages tokens
  void,
  { rejectValue: ValidationErrors }
>('auth/requestToBecomeOrganizer', async (_, { rejectWithValue }) => {
  try {
    const response = await api.post('/user/request/organizer')
    return response.data.user
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

// router
//   .route("/admin/requests")
//   .get(isAuthenticatedUser, authorizeRoles("admin"), getAllRequests);

export const getAllRequests = createAsyncThunk<
  Request[],
  void,
  { rejectValue: ValidationErrors }
>('auth/getAllRequests', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/admin/requests')
    return response.data.requests
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const approveOrRejectOrganizerRequest = createAsyncThunk<
  {
    message: string
    request: Request
    unreadNotifications: number
  },
  { id: String; status: String },
  { rejectValue: ValidationErrors }
>(
  'auth/approveOrRejectOrganizerRequest',
  async ({ id, status }, { rejectWithValue }) => {
    console.log('id', id, 'status', status, 'in thunk')

    try {
      const response = await api.put(`/admin/organizerrole/${id}`, { status })
      return response.data
    } catch (err) {
      const error = err as AxiosError<any>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

// router.route("/user/follow/:id").put(isAuthenticatedUser, followUser);
export const followUser = createAsyncThunk<
  { user: User; message: string },
  string,
  { rejectValue: ValidationErrors }
>('auth/followUser', async (userId, { rejectWithValue }) => {
  try {
    const response = await api.put(`/user/follow/${userId}`)
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

// router
//   .route("/user/update/account/address/:id")
//   .put(isAuthenticatedUser, updateUserAccountAddress);
export const updateUserAccountAddress = createAsyncThunk<
  User,
  { id: string; address: string },
  { rejectValue: ValidationErrors }
>(
  'auth/updateUserAccountAddress',
  async ({ id, address }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/user/update/account/address/${id}`, {
        address,
      })
      return response.data.user
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

export const getUserBalance = createAsyncThunk<
  { balance: number },
  void,
  { rejectValue: ValidationErrors }
>('auth/getUserBalance', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/curruser/balance')
    return response.data
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const getCurrentUser = createAsyncThunk<
  User,
  void,
  { rejectValue: ValidationErrors }
>('auth/getCurrentUser', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/me')
    return response.data.user
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})
